import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../services/api';
import { formatDuration } from '../../utils/duration';

const formatDate = (dateString) => new Date(dateString).toLocaleString();

const OnlineUsersPage = () => {
  const navigate = useNavigate();
  const { activeUsersCount } = useSocket();
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        const response = await api.get('/sessions/active');
        setActiveUsers(response.data.data || []);
      } catch (error) {
        console.error('Error fetching active users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveUsers();
  }, [activeUsersCount]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/admin/user-activity')}
          className="text-admin-slate-500 dark:text-admin-slate-400 hover:text-admin-slate-700 dark:hover:text-admin-slate-200"
          aria-label="Back to User Activity"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-admin-slate-900 dark:text-admin-slate-100">Currently Online Users</h1>
          <p className="text-sm text-admin-slate-600 dark:text-admin-slate-400">
            Users active within the last 15 minutes
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : activeUsers.length === 0 ? (
          <p className="text-admin-slate-500 dark:text-admin-slate-400 text-center py-8">
            No users currently online
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeUsers.map((session) => (
              <div key={session._id} className="border border-admin-slate-200 dark:border-admin-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-admin-ucla-100 dark:bg-admin-ucla-900/30 flex items-center justify-center">
                      <span className="text-admin-ucla-600 dark:text-admin-ucla-400 font-medium">
                        {session.userId?.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-admin-slate-900 dark:text-admin-slate-100">
                        {session.userId?.name || 'Unknown User'}
                      </p>
                      <p className="text-sm text-admin-slate-500 dark:text-admin-slate-400">
                        {session.userId?.email || 'No email'}
                      </p>
                    </div>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                </div>
                <div className="mt-3 pt-3 border-t border-admin-slate-200 dark:border-admin-slate-700">
                  <div className="flex justify-between text-sm">
                    <span className="text-admin-slate-500 dark:text-admin-slate-400">Login:</span>
                    <span className="text-admin-slate-700 dark:text-admin-slate-300">
                      {formatDate(session.loginTime)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-admin-slate-500 dark:text-admin-slate-400">Duration:</span>
                    <span className="text-admin-slate-700 dark:text-admin-slate-300">
                      {formatDuration(new Date() - new Date(session.loginTime))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnlineUsersPage;
